/**
 * State Portal access + entry helpers.
 * Used by refresh restore (main.js), Instructor → State Portal, and in-portal state switching.
 */

import axios from 'axios'

const US_STATE_NAME_BY_ABBR = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
}

function collectAccessibleStateCodes(user) {
  const access = Array.isArray(user?.statePortalAccess) ? user.statePortalAccess : []
  const codes = new Set()

  const addState = (rawId) => {
    const id = String(rawId || '').trim().toUpperCase()
    // Braden's Test sandbox portal is retired — real USPS states (+ DC) only.
    if (!id || id === 'BT') return
    codes.add(id)
  }

  access.forEach((entry) => {
    if (entry == null || entry === '') return
    if (typeof entry === 'string' || typeof entry === 'number') {
      addState(entry)
      return
    }
    addState(entry.abbreviation || entry.state || entry.id)
  })

  return [...codes]
}

/**
 * Build state-selector entries from the user's accessible states.
 * When a state has multiple Distribution-synced Learn orgs, each org title becomes a selectable row.
 */
export function buildStatePortalSelectorEntries(user, organizations = []) {
  const accessibleStates = collectAccessibleStateCodes(user)
  const orgsByState = new Map()

  ;(Array.isArray(organizations) ? organizations : []).forEach((org) => {
    const state = String(org?.state || '').trim().toUpperCase()
    if (!state || (accessibleStates.length && !accessibleStates.includes(state))) return
    if (!orgsByState.has(state)) orgsByState.set(state, [])
    orgsByState.get(state).push(org)
  })

  const entries = []
  const statesToRender = accessibleStates.length
    ? accessibleStates
    : [...orgsByState.keys()].sort()

  const pickAssociationOrg = (orgs = []) => {
    if (!orgs.length) return null
    const scored = orgs
      .map((org) => {
        let score = 0
        const name = String(org?.name || '').toLowerCase()
        const id = String(org?.companyID || '')
        if (org?.hasStatePortalAccessForInstructors) score += 8
        if (/state association|state portal/i.test(name)) score += 6
        if (/-org-state-association$/i.test(id) || /state-association/i.test(id)) score += 10
        if (org?.linkedNfhsCompanyID) score += 2
        return { org, score }
      })
      .sort((a, b) => b.score - a.score)
    return scored[0]?.org || orgs[0]
  }

  // One entry per state (state name) — not every school row.
  statesToRender.forEach((state) => {
    const orgs = orgsByState.get(state) || []
    const association = pickAssociationOrg(orgs)
    entries.push({
      id: state,
      state,
      companyID: association?.companyID || '',
      linkedNfhsCompanyID: association?.linkedNfhsCompanyID || '',
      hasStatePortalAccessForInstructors: Boolean(association?.hasStatePortalAccessForInstructors),
      name: US_STATE_NAME_BY_ABBR[state] || state,
      notificationCount: 0,
    })
  })

  return entries
}

export function getStatePortalEntries(user) {
  return buildStatePortalSelectorEntries(user, [])
}

export async function fetchStatePortalSelectorEntries(user) {
  const states = collectAccessibleStateCodes(user)
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_BASE_LINK}/get-state-portal-selector-options`,
      { states },
      { withCredentials: true }
    )
    return buildStatePortalSelectorEntries(user, response.data?.organizations || [])
  } catch (error) {
    console.error('Error loading state portal selector options:', error)
    return buildStatePortalSelectorEntries(user, [])
  }
}

export function userHasStatePortalAccess(user) {
  return collectAccessibleStateCodes(user).length > 0
}

function beginStatePortalSession({ mainStore, state }) {
  const normalizedState = String(state || '').trim().toUpperCase()
  if (!normalizedState || normalizedState === 'BT') {
    throw new Error('Braden\'s Test portal is no longer available. Choose a real state.')
  }
  if (mainStore.user) {
    mainStore.user.currentStatePortal = normalizedState
    mainStore.user.isCurrentlyInStatePortal = true
  }
  if (String(mainStore.currentStatePortal?.abbreviation || '').trim().toUpperCase() !== normalizedState) {
    mainStore.currentStatePortal = {
      abbreviation: normalizedState,
      name: US_STATE_NAME_BY_ABBR[normalizedState] || normalizedState,
    }
  }
  mainStore.isLoadingStatePortal = true

  void Promise.all([
    axios.post(`${import.meta.env.VITE_BASE_LINK}/set-current-state-portal`, {
      state: normalizedState,
    }),
    axios.post(`${import.meta.env.VITE_BASE_LINK}/set-active-state-portal-session`, {
      isInStatePortal: true,
    }),
  ]).catch((error) => {
    console.log(error)
  })

  return normalizedState
}

/**
 * Enter a specific state portal after multi-state selection.
 * @param {object} options
 * @param {object} options.mainStore
 * @param {import('vue-router').Router} options.router
 * @param {string} options.state
 */
export async function enterSelectedStatePortal({
  mainStore,
  router,
  state,
}) {
  const normalizedState = String(state || '').includes('::')
    ? String(state).split('::')[0]
    : state

  beginStatePortalSession({
    mainStore,
    state: normalizedState,
  })
  return router.push({ name: 'state-portal' })
}
